/**
 * frontend/src/components/ChangeRequest/ChangeRequestCard.js
 * 
 * A card component that displays a summary of a single change request.
 * It shows the title, status, description (truncated), type, impact level, and requester info.
 * Clicking the card navigates to the detailed view of the change request.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { FiUser, FiClock, FiUserCheck } from 'react-icons/fi';
import DOMPurify from 'dompurify';

const ChangeRequestCard = ({ change }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      className="change-card"
      onClick={() => navigate(`/dashboard/change/${change.id}`)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.2 }}
    >
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h6 className="fw-bold mb-0" style={{ color: 'var(--gray-900)' }}>
          {change.title}
        </h6>
        <span className={`status-badge status-${change.status}`}>
          {change.status.replace('_', ' ')}
        </span>
      </div>

      <div
        className="text-muted small mb-3"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(change.description) }}
      />

      <div className="d-flex gap-2 mb-3">
        <span className={`type-badge type-${change.change_type}`}>
          {change.change_type}
        </span>
        <span className={`impact-badge impact-${change.impact_level}`}>
          {change.impact_level} impact
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center text-muted small">
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center">
            <FiUser className="me-1" />
            {change.requester_name}
          </div>
          {change.assignee_name && (
            <div className="d-flex align-items-center" style={{ color: '#6366f1' }}>
              <FiUserCheck className="me-1" />
              {change.assignee_name}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center">
          <FiClock className="me-1" />
          {formatDistanceToNow(new Date(change.created_at), { addSuffix: true })}
        </div>
      </div>
    </motion.div>
  );
};

export default ChangeRequestCard;
